// src/pages/pengamatan/buah-alami/BuahAlamiDetailPage.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Typography,
    Grid,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip
} from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, off } from "firebase/database";

// Import komponen tab
import DataObservasiTab from "./detail-tabs/DataObservasiTab";
import ProsentaseTab from "./detail-tabs/ProsentaseTab";
import PopulasiPerHaTab from "./detail-tabs/PopulasiPerHaTab";
import PopulasiPerPlotTab from "./detail-tabs/PopulasiPerPlotTab";
import TonaseBuahPerHaTab from "./detail-tabs/TonaseBuahPerHaTab";
import TonaseBuahPerPlotTab from "./detail-tabs/TonaseBuahPerPlotTab";
import RencanaPanenBuahAlamiTab from "./detail-tabs/RencanaPanenBuahAlamiTab";

// Import komponen breadcrumbs
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

const BuahAlamiDetailPage = () => {
    const { pengamatanKey } = useParams();
    const location = useLocation();
    
    // State untuk tabs - DIPERBAIKI: Gunakan localStorage untuk menyimpan tab terakhir
    const [currentTab, setCurrentTab] = useState(() => {
        // Coba ambil dari localStorage saat komponen pertama kali dimuat
        const savedTab = localStorage.getItem('buah-alami-detail-current-tab');
        // Validasi tab yang disimpan
        const validTabs = [
            'data-observasi', 
            'prosentase', 
            'populasi-per-ha', 
            'populasi-per-plot', 
            'tonase-buah-per-ha', 
            'tonase-buah-per-plot', 
            'rencana-panen-buah-alami'
        ];
        return validTabs.includes(savedTab) ? savedTab : 'data-observasi';
    });

    const [pengamatanData, setPengamatanData] = useState(null);
    const [lokasiData, setLokasiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sourceTab, setSourceTab] = useState(""); // Untuk mengetahui dari tab mana user datang

    // FUNGSI BARU: Simpan tab ke localStorage
    const saveTabToLocalStorage = (tab) => {
        try {
            localStorage.setItem('buah-alami-detail-current-tab', tab);
        } catch (error) {
            console.warn('Gagal menyimpan tab detail ke localStorage:', error);
        }
    };

    // FUNGSI YANG DIPERBAIKI: Handle perubahan tab - SEKARANG MENYIMPAN KE localStorage
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        saveTabToLocalStorage(newValue); // Simpan tab yang baru dipilih
    };

    // Fungsi untuk mengambil data pengamatan
    useEffect(() => {
        if (!pengamatanKey) {
            setError("ID Pengamatan tidak valid");
            setLoading(false);
            return;
        }

        // Cek query parameter untuk mengetahui dari tab mana
        const searchParams = new URLSearchParams(location.search);
        const fromTab = searchParams.get('from');
        setSourceTab(fromTab || "");

        const pengamatanRef = ref(database, `db_freshpine/db_pengamatan/buah_alami/${pengamatanKey}`);
        
        const unsubscribe = onValue(pengamatanRef, (snapshot) => {
            const data = snapshot.val();
            
            if (!data) {
                setError("Data pengamatan tidak ditemukan");
                setLoading(false);
                return;
            }

            setPengamatanData(data);
            
            // Ambil data lokasi
            if (data.id_lokasi) {
                const lokasiRef = ref(database, `db_freshpine/tb_lokasi`);
                onValue(lokasiRef, (lokasiSnapshot) => {
                    let foundLokasi = null;
                    lokasiSnapshot.forEach((childSnapshot) => {
                        const lokasiItem = childSnapshot.val();
                        if (lokasiItem.id_lokasi === data.id_lokasi) {
                            foundLokasi = lokasiItem;
                        }
                    });
                    setLokasiData(foundLokasi);
                    setLoading(false);
                }, (error) => {
                    console.error("Error fetching location data:", error);
                    setLoading(false);
                });
            } else {
                setLoading(false);
            }
        }, (error) => {
            console.error("Error fetching observation data:", error);
            setError("Gagal memuat data pengamatan");
            setLoading(false);
        });

        return () => {
            off(pengamatanRef);
        };
    }, [pengamatanKey, location.search]);

    // Fungsi untuk mendapatkan warna status
    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved':
                return 'success';
            case 'Processed':
                return 'info';
            case 'Pending':
                return 'warning';
            case 'Draft':
                return 'default';
            default:
                return 'error';
        }
    };

    // Fungsi untuk mendapatkan data plot yang diurutkan - DIMODIFIKASI
    const getSortedPlots = () => {
        if (!pengamatanData?.plot) return [];
        
        let plots = Object.entries(pengamatanData.plot)
            .map(([plotKey, plotData]) => ({
                plotKey,
                ...plotData
            }));

        // FILTER BERDASARKAN TAB SUMBER MENGGUNAKAN OBJECT LOOKUP
        const tabFilters = {
            'Pending': 'Pending',
            'Approved': 'Approved', 
            'Processed': 'Processed'
            // 'Closing' tidak perlu filter, tampilkan semua
        };

        const filterStatus = tabFilters[sourceTab];
        if (filterStatus) {
            plots = plots.filter(plot => plot.status_pengamatan_per_plot === filterStatus);
        }
        
        // Urutkan plot
        return plots.sort((a, b) => {
            const plotA = a.plot || '';
            const plotB = b.plot || '';
            
            if (!isNaN(plotA) && !isNaN(plotB)) {
                return parseInt(plotA) - parseInt(plotB);
            }
            
            return plotA.localeCompare(plotB);
        });
    };

    // Fungsi untuk memproses data bibit
    const processBibitData = (bibit) => {
        if (!bibit || bibit.trim() === '') {
            return {
                varietas: 'MD2',
                kelas: 'RC',
                jenis: 'RC'
            };
        }

        // Ambil karakter 3-5 untuk varietas
        const varietas = bibit.length >= 5 ? bibit.substring(2, 5) : 'MD2';
        
        // Ambil karakter 6 untuk jenis
        let jenis = bibit.length >= 6 ? bibit.charAt(5) : 'RC';
        
        // LOGIKA BARU: Konversi karakter jenis
        if (jenis === 'S') {
            jenis = 'SC';
        } else if (jenis === 'C') {
            jenis = 'CR';
        } else if (jenis === 'N'){
            jenis = 'NR'
        }
        // Jika bukan S atau C, biarkan nilai aslinya (termasuk 'R' untuk RC)
        
        // Ambil karakter 7 untuk kelas
        const kelas = bibit.length >= 7 ? bibit.charAt(6) : 'RC';

        return {
            varietas,
            kelas,
            jenis
        };
    };

    // Fungsi untuk menghitung Tanggal Std Forcing
    const calculateTanggalStdForcing = () => {
        if (!lokasiData?.mulai_rawat || !bibitData.kelas) return '-';
        
        try {
            // Konversi kelas bibit ke nilai hari
            let hariTambahan;
            switch (bibitData.kelas) {
                case 'K':
                    hariTambahan = 16;
                    break;
                case 'S':
                    hariTambahan = 14;
                    break;
                case 'B':
                    hariTambahan = 12;
                    break;
                case 'RC':
                    hariTambahan = 8;
                    break;
                default:
                    hariTambahan = 8;
            }
            
            // Parse tanggal awal rawat
            const awalRawat = new Date(lokasiData.mulai_rawat);
            if (isNaN(awalRawat.getTime())) return '-';
            
            // Hitung tanggal std forcing: (hariTambahan * 30.4) + awalRawat
            const stdForcingDate = new Date(awalRawat);
            stdForcingDate.setDate(stdForcingDate.getDate() + Math.round(hariTambahan * 30.4));
            
            // Format ke DD-MM-YYYY
            const day = String(stdForcingDate.getDate()).padStart(2, '0');
            const month = String(stdForcingDate.getMonth() + 1).padStart(2, '0');
            const year = stdForcingDate.getFullYear();
            
            return `${day}-${month}-${year}`;
            
        } catch (error) {
            console.error("Error calculating Tanggal Std Forcing:", error);
            return '-';
        }
    };

    // Fungsi untuk mengubah format tanggal dari YYYY-MM-DD ke DD-MM-YYYY
    const formatDateToDDMMYYYY = (dateString) => {
        if (!dateString || dateString === '-' || dateString.trim() === '') return '-';
        
        try {
            // Jika sudah dalam format DD-MM-YYYY, return as is
            if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
                return dateString;
            }
            
            // Jika format YYYY-MM-DD
            if (dateString.includes('-') && dateString.split('-')[0].length === 4) {
                const [year, month, day] = dateString.split('-');
                return `${day}-${month}-${year}`;
            }
            
            return dateString;
        } catch (error) {
            return '-';
        }
    };

    // Breadcrumbs items
    const breadcrumbItems = [
        { label: "Dashboard", href: "/" },
        { label: "Pengamatan", href: "/pengamatan" },
        { label: "Buah Alami", href: "/pengamatan/buah-alami" },
        { label: `Detail - ${pengamatanKey}`, isActive: true }
    ];

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!pengamatanData) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">Data pengamatan tidak ditemukan</Alert>
            </Box>
        );
    }

    const bibitData = processBibitData(lokasiData?.bibit);
    const formattedAwalRawat = formatDateToDDMMYYYY(lokasiData?.mulai_rawat);
    const tanggalStdForcing = calculateTanggalStdForcing();

    return (
        <Box className="buah-alami-container" sx={{ padding: 0 }}>
            <CustomBreadcrumbs breadcrumbItems={breadcrumbItems} />

            <Card>
                <CardContent sx={{ width: "100%", overflow: "hidden", p: 2 }}>
                    {/* Header */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                            Detail Pengamatan Buah Alami Dan Populasi
                        </Typography>
                        
                        {/* Mandor dan Pengamat */}
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                mb: 2,
                                justifyContent: "space-between",
                            }}
                            >
                            <Grid item xs={6}>
                                <Typography variant="body1" color="text.secondary">
                                    <strong>Mandor:</strong> {pengamatanData.nama_mandor || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                <Typography variant="body1" color="text.secondary">
                                    <strong>Pengamat:</strong> {pengamatanData.nama_pengamat || '-'}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Header Tabel - STRUKTUR BARU */}
                        <Paper variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider',
                                                width: '12%'
                                            }}
                                            rowSpan={2}
                                        >
                                            Tanggal Pengamatan
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider',
                                                width: '12%'
                                            }}
                                            rowSpan={2}
                                        >
                                            Tanggal Std Forcing
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider',
                                                width: '8%'
                                            }}
                                            rowSpan={2}
                                        >
                                            Umur
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider',
                                                width: '12%'
                                            }}
                                            rowSpan={2}
                                        >
                                            Lokasi
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider',
                                                width: '8%'
                                            }}
                                            rowSpan={2}
                                        >
                                            Status
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider',
                                                width: '15%',
                                                textAlign: 'center'
                                            }}
                                            colSpan={3}
                                        >
                                            Bibit
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold', 
                                                width: '10%'
                                            }}
                                            rowSpan={2}
                                        >
                                            Awal Rawat
                                        </TableCell>
                                    </TableRow>
                                    {/* Sub-header untuk Bibit */}
                                    <TableRow>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold',
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider'
                                            }}
                                        >
                                            Varietas
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold',
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider'
                                            }}
                                        >
                                            Kelas
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                fontWeight: 'bold',
                                                borderRight: '1px solid',
                                                borderRightColor: 'divider'
                                            }}
                                        >
                                            Jenis
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* HANYA SATU BARIS DATA - tidak perlu sesuai jumlah plot */}
                                    <TableRow>
                                        {/* Tanggal Pengamatan */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {pengamatanData.tanggal_pengamatan || '-'}
                                        </TableCell>
                                        
                                        {/* Tanggal Std Forcing - SEMENTARA - */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {tanggalStdForcing}
                                        </TableCell>
                                        
                                        {/* FMP */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {pengamatanData.fmp || '-'}
                                        </TableCell>
                                        
                                        {/* Lokasi */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {pengamatanData.lokasi || '-'}
                                        </TableCell>
                                        
                                        {/* Status */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {lokasiData?.status || '-'}
                                        </TableCell>
                                        
                                        {/* Bibit - Varietas */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {bibitData.varietas}
                                        </TableCell>
                                        
                                        {/* Bibit - Kelas */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {bibitData.kelas}
                                        </TableCell>
                                        
                                        {/* Bibit - Jenis */}
                                        <TableCell sx={{ borderRight: '1px solid', borderRightColor: 'divider' }}>
                                            {bibitData.jenis}
                                        </TableCell>
                                        
                                        {/* Awal Rawat - FORMAT DD-MM-YYYY */}
                                        <TableCell>
                                            {formattedAwalRawat}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>

                    {/* Tabs - DITAMBAHKAN 3 TAB BARU */}
                    <Box 
                        sx={{ 
                            mb: 2,
                            backgroundColor: '#f2f9ff',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                '& .MuiTab-root': {
                                    fontWeight: 'bold',
                                    minWidth: 120,
                                    py: 1.5,
                                    fontSize: '0.875rem',
                                    color: 'text.primary',
                                    '&.Mui-selected': {
                                        color: 'primary.main',
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'primary.main',
                                    height: 3
                                }
                            }}
                        >
                            <Tab label="Observasi" value="data-observasi" />
                            <Tab label="Prosentase" value="prosentase" />
                            <Tab label="Populasi Per HA" value="populasi-per-ha" />
                            <Tab label="Populasi Per Plot" value="populasi-per-plot" />
                            <Tab label="Tonase Buah Per HA" value="tonase-buah-per-ha" />
                            <Tab label="Tonase Buah Per Plot" value="tonase-buah-per-plot" />
                            <Tab label="Rencana Panen Buah Alami" value="rencana-panen-buah-alami" />
                        </Tabs>
                    </Box>

                    {/* Tab Content - DITAMBAHKAN 3 TAB BARU */}
                    <Box sx={{ mt: 2 }}>
                        {currentTab === "data-observasi" && (
                            <DataObservasiTab 
                                pengamatanData={pengamatanData}
                                filteredPlots={getSortedPlots()}
                                sourceTab={sourceTab}
                            />
                        )}
                        {currentTab === "prosentase" && (
                            <ProsentaseTab 
                                pengamatanData={pengamatanData}
                                filteredPlots={getSortedPlots()}
                                sourceTab={sourceTab}
                            />
                        )}
                        {currentTab === "populasi-per-ha" && (
                            <PopulasiPerHaTab 
                                pengamatanData={pengamatanData}
                                filteredPlots={getSortedPlots()}
                                sourceTab={sourceTab}
                            />
                        )}
                        {currentTab === "populasi-per-plot" && (
                            <PopulasiPerPlotTab 
                                pengamatanData={pengamatanData}
                                filteredPlots={getSortedPlots()}
                                sourceTab={sourceTab}
                            />
                        )}
                        {currentTab === "tonase-buah-per-ha" && (
                            <TonaseBuahPerHaTab 
                                pengamatanData={pengamatanData}
                                filteredPlots={getSortedPlots()}
                                sourceTab={sourceTab}
                                
                            />
                        )}
                        {currentTab === "tonase-buah-per-plot" && (
                            <TonaseBuahPerPlotTab 
                                pengamatanData={pengamatanData}
                                filteredPlots={getSortedPlots()}
                                sourceTab={sourceTab}
                            />
                        )}
                        {currentTab === "rencana-panen-buah-alami" && (
                            <RencanaPanenBuahAlamiTab 
                                pengamatanData={pengamatanData}
                                filteredPlots={getSortedPlots()}
                                sourceTab={sourceTab}
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default BuahAlamiDetailPage;